import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Chip, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { OnlyFansPosts } from '../../hooks/useSubscriberPosts';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditonal from '../../hooks/useSubscriberVaultMediasAdditional';
import { stripHtmlAndConvertParagraphs } from '../../utils/common';

const OnlyFansPostPreview = ({ post }: { post: OnlyFansPosts.List }) => {
    const { getManyVaultMediaFromDatabase, isInventoryVaultItemsLoading } = useSubscribersVaultMediasAdditonal();

    const [vaultMedias, setVaultMedias] = useState<VaultMediaResponseList[]>([]);

    useEffect(() => {
        const fetchVaultMedias = async () => {
            if (post.media && post.media.length > 0) {
                const mediaIds = post.media.map(media => media.id);
                const medias = await getManyVaultMediaFromDatabase(mediaIds);
                setVaultMedias(medias);
            }
        };

        fetchVaultMedias();
    }, [post.media]);

    return (
        <Alert icon={false} severity="success">
            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1">Post Preview</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" className="notranslate">
                        {stripHtmlAndConvertParagraphs(post.text)}
                    </Typography>
                </Grid>
                {post.media && post.media.length > 0 && (
                    <Grid item xs={12}>
                        {/** Horizontal scroll */}
                        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                            <ChevronLeftIcon
                                sx={{ alignSelf: 'center', cursor: 'pointer' }}
                                onClick={() => {
                                    const container = document.querySelector('#post-media-scroll-container');
                                    if (container) {
                                        container.scrollBy({ left: -150, behavior: 'smooth' });
                                    }
                                }}
                            />
                            <Box
                                id="post-media-scroll-container"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 1,
                                    overflowX: 'auto',
                                    scrollBehavior: 'smooth',
                                }}
                            >
                                {post.media.map((media, index) => {
                                    return (
                                        <Box
                                            key={media.id}
                                            width={100}
                                            height={100}
                                            minWidth={100}
                                            minHeight={100}
                                            sx={{
                                                bgcolor: 'gray.200',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {isInventoryVaultItemsLoading && (
                                                <Skeleton variant="rectangular" width={'100%'} height={'100%'} />
                                            )}
                                            {!isInventoryVaultItemsLoading && media.type !== 'audio' && (
                                                <Box
                                                    component="img"
                                                    src={vaultMedias.find(vaultMedia => vaultMedia.id === media.id)?.cachedThumb || ''}
                                                    alt={media.type}
                                                    loading="lazy"
                                                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                                                />
                                            )}
                                            {!isInventoryVaultItemsLoading && media.type === 'audio' && <AudiotrackRoundedIcon />}
                                        </Box>
                                    );
                                })}
                            </Box>
                            <ChevronRightIcon
                                sx={{
                                    alignSelf: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    const container = document.querySelector('#post-media-scroll-container');
                                    if (container) {
                                        container.scrollBy({ left: 150, behavior: 'smooth' });
                                    }
                                }}
                            />
                        </Stack>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="column" spacing={0}>
                        <div>Posted At: {moment(post.postedAt).format('LLL')}</div>
                        {post.expiredAt && <div>Expired At: {moment(post.expiredAt).format('LLL')}</div>}
                    </Stack>
                </Grid>
                {post.fundRaising && post.fundRaising.presets.length > 0 && (
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">Tip Options:</Typography>
                            {post.fundRaising?.presets.map(preset => (
                                <Chip
                                    color="success"
                                    size="small"
                                    label={`$${parseFloat(preset).toLocaleString(undefined, {
                                        style: 'decimal',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}`}
                                    key={preset}
                                />
                            ))}
                        </Stack>
                    </Grid>
                )}
                {post.fundRaising && post.fundRaising.target && (
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">Progress/Target:</Typography>
                            <Chip
                                color={post.fundRaising.targetProgress >= post.fundRaising.target ? 'success' : 'warning'}
                                size="small"
                                label={`${dinero({
                                    amount: Math.trunc(post.fundRaising.targetProgress * 100),
                                    currency: 'USD',
                                }).toFormat()} / ${dinero({
                                    amount: Math.trunc(post.fundRaising.target * 100),
                                    currency: 'USD',
                                }).toFormat()}`}
                            />
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Alert>
    );
};

export default OnlyFansPostPreview;
